import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '#environments';
import {
  PaCaseDetail,
  PaCancellationRequest,
  PaStageParticipant,
  PaRequestDetail,
  SavePaRequestRequest,
  PaLandReviewDetail,
  SavePaLandReviewRequest,
  PaSurfaceCostEstimateDetail,
  SavePaSurfaceCostRequest,
  PaSurfaceSurveyDetail,
  SavePaSurfaceSurveyRequest,
  PaAfeReviewDetail,
  SavePaAfeReviewRequest,
  PaWorkOrderDetail,
  SavePaWorkOrderRequest,
  SavePaWorkOrderWellInfoRequest,
  PaCentralMaintenanceDetail,
  SavePaCentralMaintenanceWellInfoRequest,
  PaDigPlanDetail,
  SavePaDigPlanRequest,
  SavePaDigPlanWellInfoRequest,
  PaSchedulingDetail,
  SavePaSchedulingRequest,
  SavePaSchedulingWellInfoRequest,
  SavePaWellviewRequest,
  PaExecutionDetail,
  SavePaExecutionRequest,
  SavePaExecutionWellInfoRequest,
  PaWellheadCutoffDetail,
  SavePaWellheadCutoffRequest,
  SavePaWellheadCutoffWellInfoRequest,
  PaWellheadCutoffSendBackRequest,
  PaCommentDto,
  AddPaCommentRequest,
  PaJobCloseOutDetail,
  SavePaJobCloseOutRequest,
  RLLookupOption,
  AuditLogEntry,
  PaAuditLogEntry,
  CpAttachment,
  CpCorrespondenceItem,
  PaStageQueueItem,
  PaStageQueuePage,
  PaTitleClearanceQueueItem,
  PaRigQueueRow,
  UpdatePaRigQueueItem,
} from '#models';

@Injectable({
  providedIn: 'root',
})
export class PaCasesApiService {
  private _http = inject(HttpClient);
  private readonly _baseUrl = `${environment.apiUrl}/pa-cases`;

  // ─── Case ─────────────────────────────────────────────────────────────────

  getCase(id: number): Observable<PaCaseDetail> {
    return this._http.get<PaCaseDetail>(`${this._baseUrl}/${id}`);
  }

  getCaseByCaseNumber(caseNumber: string): Observable<PaCaseDetail> {
    return this._http.get<PaCaseDetail>(`${this._baseUrl}/by-number/${encodeURIComponent(caseNumber)}`);
  }

  cancelCase(id: number, request: PaCancellationRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/cancel`, request);
  }

  getContacts(id: number): Observable<PaStageParticipant[]> {
    return this._http.get<PaStageParticipant[]>(`${this._baseUrl}/${id}/contacts`);
  }

  // ─── Attachments ────────────────────────────────────────────────────────────

  getAttachments(id: number): Observable<CpAttachment[]> {
    return this._http.get<CpAttachment[]>(`${this._baseUrl}/${id}/attachments`);
  }

  uploadAttachment(id: number, file: File, attachmentType: string): Observable<CpAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('attachmentType', attachmentType);
    return this._http.post<CpAttachment>(`${this._baseUrl}/${id}/attachments`, formData);
  }

  deleteAttachment(id: number, attachmentId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}/attachments/${attachmentId}`);
  }

  getAttachmentUrl(id: number, attachmentId: number): Observable<{ downloadUrl: string }> {
    return this._http.get<{ downloadUrl: string }>(`${this._baseUrl}/${id}/attachments/${attachmentId}/download`);
  }

  // ─── Audit Log (FR-015) ──────────────────────────────────────────────────

  getAuditLog(id: number): Observable<PaAuditLogEntry[]> {
    return this._http.get<AuditLogEntry[]>(`${this._baseUrl}/${id}/audit-log`).pipe(
      map(entries => entries.map(e => ({ ...e, displayDescription: e.actionType }))),
    );
  }

  // ─── Correspondence ───────────────────────────────────────────────────────

  getCorrespondence(id: number): Observable<CpCorrespondenceItem[]> {
    return this._http.get<CpCorrespondenceItem[]>(`${this._baseUrl}/${id}/correspondence`);
  }

  getCorrespondenceDownloadUrl(id: number, logId: number): Observable<{ downloadUrl: string }> {
    return this._http.get<{ downloadUrl: string }>(`${this._baseUrl}/${id}/correspondence/${logId}/download`);
  }

  // ─── Comments (FR-018, FR-019) ────────────────────────────────────────────

  getComments(id: number): Observable<PaCommentDto[]> {
    return this._http.get<PaCommentDto[]>(`${this._baseUrl}/${id}/comments`);
  }

  addComment(id: number, request: AddPaCommentRequest): Observable<PaCommentDto> {
    return this._http.post<PaCommentDto>(`${this._baseUrl}/${id}/comments`, request);
  }

  /** Returns the current single comment body for the case (empty string if none). */
  getComment(id: number): Observable<string> {
    return this._http.get<{ body: string | null }>(`${this._baseUrl}/${id}/comment`)
      .pipe(map(r => r.body ?? ''));
  }

  /** Saves (replaces) the single editable comment for the case. Pass empty string to clear. */
  saveComment(id: number, body: string): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/comment`, { body });
  }

  // ─── Request stage ────────────────────────────────────────────────────────

  getRequest(id: number): Observable<PaRequestDetail> {
    return this._http.get<PaRequestDetail>(`${this._baseUrl}/${id}/request`);
  }

  saveRequest(id: number, request: SavePaRequestRequest, submit = false): Observable<PaCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/request`, request, { params });
  }

  // ─── Land Review stage ────────────────────────────────────────────────────

  getLandReview(id: number): Observable<PaLandReviewDetail> {
    return this._http.get<PaLandReviewDetail>(`${this._baseUrl}/${id}/land-review`);
  }

  saveLandReview(id: number, request: SavePaLandReviewRequest, submit = false): Observable<PaCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/land-review`, request, { params });
  }

  editLandDetails(id: number, request: SavePaLandReviewRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/edit-land-details`, request);
  }

  generateTitleClearance(id: number, landReview: SavePaLandReviewRequest): Observable<Blob> {
    return this._http.post(`${this._baseUrl}/${id}/documents/title-clearance`, landReview, { responseType: 'blob' });
  }

  /** FR-154 (CH-S01-004) — Side-effect-free preview: loads persisted Land Review from DB, returns PDF bytes. No attachment created; no notification dispatched. */
  downloadTitleClearancePreview(id: number): Observable<Blob> {
    return this._http.get(`${this._baseUrl}/${id}/documents/title-clearance-preview`, { responseType: 'blob' });
  }

  // ─── Surface Cost Estimate stage ─────────────────────────────────────────

  getSurfaceCost(id: number): Observable<PaSurfaceCostEstimateDetail> {
    return this._http.get<PaSurfaceCostEstimateDetail>(`${this._baseUrl}/${id}/surface-cost`);
  }

  saveSurfaceCost(id: number, request: SavePaSurfaceCostRequest, submit = false): Observable<PaCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/surface-cost`, request, { params });
  }

  // ─── Site Review stage (Surface Survey + AFE Review) ─────────────────────

  getSurfaceSurvey(id: number): Observable<PaSurfaceSurveyDetail> {
    return this._http.get<PaSurfaceSurveyDetail>(`${this._baseUrl}/${id}/surface-survey`);
  }

  saveSurfaceSurvey(id: number, request: SavePaSurfaceSurveyRequest, submit = false): Observable<PaCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/surface-survey`, request, { params });
  }

  getSiteEvaluation(id: number): Observable<Blob> {
    return this._http.get(`${this._baseUrl}/${id}/documents/site-evaluation`, { responseType: 'blob' });
  }

  getAfeReview(id: number): Observable<PaAfeReviewDetail> {
    return this._http.get<PaAfeReviewDetail>(`${this._baseUrl}/${id}/afe-review`);
  }

  saveAfeReview(id: number, request: SavePaAfeReviewRequest, submit = false): Observable<PaCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/afe-review`, request, { params });
  }

  // ─── Work Order Submission stage ─────────────────────────────────────────

  getWorkOrder(id: number): Observable<PaWorkOrderDetail> {
    return this._http.get<PaWorkOrderDetail>(`${this._baseUrl}/${id}/work-order`);
  }

  saveWorkOrder(id: number, request: SavePaWorkOrderRequest, submit = false): Observable<PaCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/work-order`, request, { params });
  }

  saveWorkOrderWellInfo(id: number, request: SavePaWorkOrderWellInfoRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/work-order/well-info`, request);
  }

  // ─── Central Maintenance stage ────────────────────────────────────────────

  getCentralMaintenance(id: number): Observable<PaCentralMaintenanceDetail> {
    return this._http.get<PaCentralMaintenanceDetail>(`${this._baseUrl}/${id}/central-maintenance`);
  }

  acknowledgeActiveWorkOrder(id: number): Observable<PaCaseDetail> {
    return this._http.post<PaCaseDetail>(`${this._baseUrl}/${id}/central-maintenance/acknowledge`, {});
  }

  submitCentralMaintenance(id: number): Observable<PaCaseDetail> {
    return this._http.post<PaCaseDetail>(`${this._baseUrl}/${id}/central-maintenance/submit`, {});
  }

  saveCentralMaintenanceWellInfo(id: number, request: SavePaCentralMaintenanceWellInfoRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/central-maintenance/well-information`, request);
  }

  // ─── Surface Dig Plan stage ───────────────────────────────────────────────

  getDigPlan(id: number): Observable<PaDigPlanDetail> {
    return this._http.get<PaDigPlanDetail>(`${this._baseUrl}/${id}/dig-plan`);
  }

  saveDigPlan(id: number, step: string, request: SavePaDigPlanRequest, submit = false): Observable<PaCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/dig-plan/${step}`, request, { params });
  }

  saveDigPlanWellInfo(id: number, request: SavePaDigPlanWellInfoRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/dig-plan/well-info`, request);
  }

  // ─── Scheduling stage ─────────────────────────────────────────────────────

  getScheduling(id: number): Observable<PaSchedulingDetail> {
    return this._http.get<PaSchedulingDetail>(`${this._baseUrl}/${id}/scheduling`);
  }

  saveScheduling(id: number, request: SavePaSchedulingRequest, submit = false): Observable<PaCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/scheduling`, request, { params });
  }

  saveSchedulingWellInfo(id: number, request: SavePaSchedulingWellInfoRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/scheduling/well-info`, request);
  }

  saveWellview(id: number, request: SavePaWellviewRequest): Observable<PaCaseDetail> {
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/wellview`, request);
  }

  /** Returns all PA cases in SS Scheduling assigned to a rig, ordered by queue position (§9.2.11). */
  getPaRigQueue(rigId: string): Observable<PaRigQueueRow[]> {
    return this._http.get<PaRigQueueRow[]>(`${this._baseUrl}/scheduling/rig-queue`, { params: { rigId } });
  }

  /** Batch-updates QueueOrder for cases in the rig's queue after a drag-and-drop reorder (§9.2.11). */
  updatePaRigQueue(rigId: string, items: UpdatePaRigQueueItem[]): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/scheduling/rig-queue`, items, { params: { rigId } });
  }

  generateConservationDocument(id: number): Observable<Blob> {
    return this._http.post(`${this._baseUrl}/${id}/documents/conservation`, {}, { responseType: 'blob' });
  }

  // ─── Execution stage ──────────────────────────────────────────────────────

  getExecution(id: number): Observable<PaExecutionDetail> {
    return this._http.get<PaExecutionDetail>(`${this._baseUrl}/${id}/execution`);
  }

  saveExecution(id: number, request: SavePaExecutionRequest, submit = false): Observable<PaCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/execution`, request, { params });
  }

  saveExecutionWellInfo(id: number, request: SavePaExecutionWellInfoRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/execution/well-info`, request);
  }

  /** GET …/execution/retrieval-partners — FR-105: Admin M012 / Callfield retrieval partner names. */
  getRetrievalPartners(id: number): Observable<string[]> {
    return this._http.get<string[]>(`${this._baseUrl}/${id}/execution/retrieval-partners`);
  }

  /** FR-099 (CR 2026-08-15) — Returns a time-limited SAS download URL for the conservation
   *  measures template, resolved via the shared artifact endpoint (same blob as RL). */
  downloadConservationMeasuresTemplate(): Observable<{ downloadUrl: string; fileName: string }> {
    return this._http.get<{ downloadUrl: string; fileName: string }>(
      `${environment.apiUrl}/artifacts/ConservationAgreementFile/download`
    );
  }

  /**
   * POST …/powerline-approval — Approve or Reject the Powerline Variance Plan (CH-S03-005, FR-110).
   * Sends explicit `{ decision: 'Approved' | 'Rejected', comments }` body (G-BE-01, G-FE-01).
   */
  submitPowerlineApproval(id: number, dto: { decision: 'Approved' | 'Rejected'; comments: string | null }): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/powerline-approval`, dto);
  }

  /** POST …/powerline-variance/request-approval — triggers approval-required notification to PASuperintendent (§9.2.12.2.1).
   *  approvalRequestor is persisted server-side first if the Execution form was never saved, so the
   *  notification's submitter field is never blank. */
  requestPowerlineVarianceApproval(id: number, approvalRequestor?: string | null): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/powerline-variance/request-approval`, { approvalRequestor: approvalRequestor ?? null });
  }

  // ─── Wellhead Cutoff stage ────────────────────────────────────────────────

  getWellheadCutoff(id: number): Observable<PaWellheadCutoffDetail> {
    return this._http.get<PaWellheadCutoffDetail>(`${this._baseUrl}/${id}/wellhead-cutoff`);
  }

  saveWellheadCutoff(id: number, request: SavePaWellheadCutoffRequest, submit = false): Observable<PaCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/wellhead-cutoff`, request, { params });
  }

  saveWellheadCutoffWellInfo(id: number, request: SavePaWellheadCutoffWellInfoRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/wellhead-cutoff/well-info`, request);
  }

  sendBackWellheadCutoff(id: number, request: PaWellheadCutoffSendBackRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/wellhead-cutoff/send-back`, request);
  }

  // ─── Job Close Out stage ──────────────────────────────────────────────────

  getJobCloseOut(id: number): Observable<PaJobCloseOutDetail> {
    return this._http.get<PaJobCloseOutDetail>(`${this._baseUrl}/${id}/close-out`);
  }

  saveJobCloseOut(id: number, request: SavePaJobCloseOutRequest, submit = false): Observable<PaCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<PaCaseDetail>(`${this._baseUrl}/${id}/close-out`, request, { params });
  }

  saveJobCloseOutWellInfo(id: number, request: SavePaWellheadCutoffWellInfoRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}/close-out/well-info`, request);
  }

  // ─── Lookups ──────────────────────────────────────────────────

  /** Returns CallFieldLookup option rows for a given group (calls the shared /api/lookup endpoint). */
  getLookupOptions(group: string): Observable<RLLookupOption[]> {
    return this._http.get<RLLookupOption[]>(
      `${environment.apiUrl}/lookup/${encodeURIComponent(group)}`
    );
  }

  /** Returns available PA rig names from the Rig table, sorted alphabetically. */
  getPaRigOptions(): Observable<string[]> {
    return this._http.get<string[]>(`${environment.apiUrl}/pa-stages/rig-options`);
  }

  // ─── Stage-queue (6.2B) ──────────────────────────────────────────

  /** GET /api/pa-cases/stage-queue?stage={stage}&siteReviewSubflow={value} — returns PA cases at the given workflow stage. */
  getStageQueue(
    stage: string,
    options?: { siteReviewSubflow?: 'surfaceSurveyPending' | 'afeReviewPending' },
  ): Observable<PaStageQueueItem[]> {
    const params: Record<string, string> = { stage };
    if (options?.siteReviewSubflow) {
      params['siteReviewSubflow'] = options.siteReviewSubflow;
    }

    return this._http.get<PaStageQueueItem[]>(`${this._baseUrl}/stage-queue`, {
      params,
    });
  }

  /**
   * GET /api/pa-cases/stage-queue-paged?stage={stage}&keyword={keyword}&pageNumber={n}&pageSize={n}
   * Paginated, keyword-searchable variant used by the Submissions dashboard's Plug & Abandonment tab.
   */
  getStageQueuePaged(
    stage: string,
    options?: {
      siteReviewSubflow?: 'surfaceSurveyPending' | 'afeReviewPending';
      keyword?: string;
      pageNumber?: number;
      pageSize?: number;
    },
  ): Observable<PaStageQueuePage> {
    const params: Record<string, string> = { stage };
    if (options?.siteReviewSubflow) {
      params['siteReviewSubflow'] = options.siteReviewSubflow;
    }
    if (options?.keyword) {
      params['keyword'] = options.keyword;
    }
    if (options?.pageNumber) {
      params['pageNumber'] = String(options.pageNumber);
    }
    if (options?.pageSize) {
      params['pageSize'] = String(options.pageSize);
    }

    return this._http.get<PaStageQueuePage>(`${this._baseUrl}/stage-queue-paged`, {
      params,
    });
  }

  /** GET /api/pa-cases/title-clearance-queue — returns PA Land Review cases pending title clearance approval. */
  getTitleClearanceQueue(): Observable<PaTitleClearanceQueueItem[]> {
    return this._http.get<PaTitleClearanceQueueItem[]>(`${this._baseUrl}/title-clearance-queue`);
  }
}
